import { ReceiptText, Clock } from 'lucide-react';
import { CoinIcon, SokoCoinIcon } from '@/app/components/SvgIcons';
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/app/lib/axios";
import { PointsLog } from "@/types/pointsLog";
import PaginationWrapper from "@/app/components/Wrappers/PaginationWrapper";
import {formatDate} from "@/Utils/FormatDate";
import {getPointsLogCurrency} from "@/Utils/pointsLog";

interface PointsHistoryResponse {
    data: PointsLog[];
    current_page: number;
    last_page: number;
    total: number;
    per_page: number;
}

export function HistoryTab({}) {
    const [currentPage, setCurrentPage] = useState(1);

    // Fetch points history with TanStack Query
    const { data, isLoading, isError, error } = useQuery<PointsHistoryResponse>({
        queryKey: ['pointsHistory', currentPage],
        queryFn: async () => {
            const response = await api.get("points", {
                params: { page: currentPage }
            });
            return response.data;
        },
        staleTime: 5000, // 5 seconds
        refetchOnWindowFocus: true,
    });

    const handlePageChange = (page: number) => {
        if (page > 0 && data && page <= data.last_page && page !== currentPage) {
            setCurrentPage(page);
            // Scroll to top of history section
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="text-center py-12 text-gray-400">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-400 mx-auto mb-3"></div>
                <p className="text-xs text-gray-400">Loading transaction history…</p>
            </div>
        );
    }

    // Error state
    if (isError) {
        return (
            <div className="text-center py-12 text-red-400">
                <ReceiptText size={48} className="mx-auto mb-3 opacity-50" />
                <p className="text-xs">Failed to load transaction history</p>
                <p className="text-xs text-gray-400 mt-2">
                    {error instanceof Error ? error.message : 'Unknown error'}
                </p>
            </div>
        );
    }

    // Empty state
    if (!data || data.data.length === 0) {
        return (
            <div className="text-center py-12 text-gray-500">
                <ReceiptText size={48} className="mx-auto mb-3 opacity-50" />
                <p className="text-xs text-gray-400">No transaction history</p>
            </div>
        );
    }

    return (
        <div>
            {/* Summary Stats */}
            <div className="mb-6 p-4 bg-gray-800/50 rounded-xl">
                <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Total transactions</span>
                    <span className="font-mono font-bold text-white tabular-nums">{data.total}</span>
                </div>
            </div>

            {/* Top Pagination */}
            <div className="mb-6">
                <PaginationWrapper
                    isLoading={isLoading}
                    currentPage={data.current_page}
                    lastPage={data.last_page}
                    onPageChange={handlePageChange}
                />
            </div>

            {/* History Items */}
            <div className="space-y-3">
                {data.data.map(item => (
                    <HistoryCard key={item.id} item={item} />
                ))}
            </div>

            {/* Bottom Pagination */}
            <div className="mt-6">
                <PaginationWrapper
                    isLoading={isLoading}
                    currentPage={data.current_page}
                    lastPage={data.last_page}
                    onPageChange={handlePageChange}
                />
            </div>
        </div>
    );
}

function HistoryCard({ item }: { item: PointsLog }) {
    const isSoko = getPointsLogCurrency(item) === 'soko_points';

    return (
        <div className="bg-gray-800/50 rounded-xl p-4 hover:bg-gray-800/70 transition-colors">
            <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold text-gray-200">{item.event_name}</div>
                <div className={`flex items-center gap-1 font-mono font-bold tabular-nums ${
                    item.type === 'increment' ? 'text-green-400' : 'text-red-400'
                }`}>
                    <span className="text-sm md:text-lg">{item.type === 'increment' ? '+' : '−'}{item.points.toLocaleString()}</span>
                    {isSoko ? <SokoCoinIcon size={14} /> : <CoinIcon size={14} />}
                </div>
            </div>
            <div className="flex items-center gap-2 text-[11px] text-gray-400">
                <Clock size={14} />
                {formatDate(item.created_at)}
            </div>
        </div>
    );
}
